import {createArchitecture} from './architecture.js';
import {createBrandInk} from './ink-surface.js';

const clamp = value => Math.max(0, Math.min(1, value));
const smooth = (start, end, value) => {const p = clamp((value - start) / (end - start)); return p * p * (3 - 2 * p);};
const mix = (a, b, p) => a + (b - a) * p;

// A — 一筆成屋：光的誕生. Every frame derives from absolute time.
export function createA({THREE, scene, camera, renderer, portrait = false}) {
  const architecture = createArchitecture({THREE, scene, portrait});
  const ink = createBrandInk({THREE, scene});
  let isPortrait = !!portrait, currentTime = 0, disposed = false;
  let path = null, frame = null, stroke = null;
  const target = new THREE.Vector3(), look = new THREE.Vector3();
  const initialPosition = new THREE.Vector3(.1, 2.25, 20.3);
  const initialTarget = new THREE.Vector3(.1, 2.25, 14.2);
  const lineUniforms = {uProgress: {value: 0}, uOpacity: {value: 0}, uColor: {value: new THREE.Color('#d8c5a4')}};
  const lineMaterial = new THREE.ShaderMaterial({
    uniforms: lineUniforms, transparent: true, depthWrite: false, side: THREE.DoubleSide,
    vertexShader: 'varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}',
    fragmentShader: `uniform float uProgress,uOpacity;uniform vec3 uColor;varying vec2 vUv;void main(){
      float edge=1.-smoothstep(uProgress-.009,uProgress+.003,vUv.x);
      float fibre=.84+.16*sin(vUv.y*31.4159+sin(vUv.x*43.)*.7);
      float fresh=exp(-abs(uProgress-vUv.x)*150.)*.15;
      gl_FragColor=vec4(uColor*(fibre+fresh),edge*uOpacity);
      #include <tonemapping_fragment>
      #include <colorspace_fragment>
    }`
  });

  function compose() {
    frame = architecture.finalCamera(isPortrait);
    path = new THREE.CatmullRomCurve3([
      initialPosition.clone(), new THREE.Vector3(.24, 2.17, 15.1),
      new THREE.Vector3(-.08, 1.90, 9.6), new THREE.Vector3(.15, 1.73, 4.15), new THREE.Vector3(...frame.position)
    ], false, 'centripetal', .42);
    const fov = isPortrait ? 42 : 36;
    const distance = initialPosition.distanceTo(initialTarget);
    const visibleWidth = Math.tan(THREE.MathUtils.degToRad(fov) / 2) * distance * 2 * camera.aspect;
    ink.setWidth(visibleWidth * (isPortrait ? .82 : .44));
    const origin = ink.point(781.62, 335.90);
    const curve = new THREE.CurvePath();
    curve.add(new THREE.CubicBezierCurve3(origin, origin.clone().add(new THREE.Vector3(.4, -.2, -.3)), new THREE.Vector3(1.82, .42, 12.6), new THREE.Vector3(1.82, .04, 11.8)));
    // The final ink release becomes the actual entrance's floor and jambs.
    for (const [a, b] of [
      [[1.82,.04,11.8],[-1.62,.04,11.8]], [[-1.62,.04,11.8],[-1.62,3.55,11.8]],
      [[-1.62,3.55,11.8],[1.82,3.55,11.8]], [[1.82,3.55,11.8],[1.82,.04,11.8]],
      [[1.82,.04,11.8],[1.82,.04,7.2]]
    ]) curve.add(new THREE.LineCurve3(new THREE.Vector3(...a), new THREE.Vector3(...b)));
    const geometry = new THREE.TubeGeometry(curve, 280, isPortrait ? .011 : .013, 6, false);
    if (stroke) {stroke.geometry.dispose(); stroke.geometry = geometry;}
    else {stroke = new THREE.Mesh(geometry, lineMaterial); stroke.frustumCulled = false; stroke.renderOrder = 5; scene.add(stroke);}
  }

  function update(seconds) {
    if (disposed) return;
    currentTime = Math.max(0, Math.min(5.8, Number.isFinite(seconds) ? seconds : 0));
    const time = currentTime;
    const travel = smooth(1.74, 4.66, time), reveal = smooth(2.88, 4.45, time);
    const wire = smooth(1.86, 2.82, time) * (1 - smooth(3.27, 4.35, time));
    architecture.update({surface: reveal, line: wire, light: smooth(2.90, 4.58, time), time});
    renderer.toneMappingExposure = mix(.98, 1.08, smooth(2.85, 4.55, time));
    ink.update(time, smooth(1.73, 2.37, time), 1 - smooth(2.24, 2.4, time));
    lineUniforms.uProgress.value = smooth(1.64, 2.72, time);
    lineUniforms.uOpacity.value = smooth(1.64, 1.82, time) * (1 - smooth(2.78, 3.37, time));
    stroke.visible = lineUniforms.uOpacity.value > .001;
    camera.position.copy(path.getPointAt(travel));
    target.set(...frame.target);
    const ahead = new THREE.Vector3(camera.position.x * .35, mix(2.25, isPortrait ? 1.94 : 1.70, travel), camera.position.z - 6.1);
    look.copy(initialTarget).lerp(ahead, smooth(1.74, 2.35, time));
    look.lerp(target, smooth(3.70, 4.66, time));
    camera.lookAt(look);
    const fov = mix(isPortrait ? 42 : 36, frame.fov, smooth(2.05, 4.66, time));
    if (camera.fov !== fov) {camera.fov = fov; camera.updateProjectionMatrix();}
    if (time >= 4.66) {camera.position.set(...frame.position); camera.lookAt(...frame.target);}
  }

  compose(); update(0);
  return {
    duration: 5.8,
    update,
    resize(nextPortrait) {
      isPortrait = !!nextPortrait; architecture.resize?.(isPortrait);
      compose(); update(currentTime);
    },
    inspect() {
      return {concept: 'A — 一筆成屋：光的誕生', seconds: currentTime, duration: 5.8,
        frame: isPortrait ? 'portrait 9:19.5' : 'landscape 16:9',
        phase: currentTime < 1.7 ? '原字・蘸墨與提筆' : currentTime < 3.1 ? '一筆成為門框・推入透視' : currentTime < 4.75 ? '日光顯露石材、橡木與灰泥' : '共用品牌落版',
        handwriting: ink.metadata, camera: camera.position.toArray().map(value => +value.toFixed(4)),
        target: look.toArray().map(value => +value.toFixed(4)), architecturalStrokeProgress: +lineUniforms.uProgress.value.toFixed(3)};
    },
    dispose() {
      if (disposed) return; disposed = true; architecture.dispose(); ink.dispose();
      if (stroke) {scene.remove(stroke); stroke.geometry.dispose();} lineMaterial.dispose();
    }
  };
}
